import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { 
  Smartphone, 
  Server, 
  Globe, 
  Brain, 
  Palette, 
  Handshake,
  ArrowRight,
  CheckCircle
} from "lucide-react";

const services = [
  {
    id: "mobile",
    icon: Smartphone,
    title: "mobileAppDev",
    description: "mobileAppDesc",
    gradient: "from-primary to-primary/80",
    technologies: ["Flutter", "Dart", "Firebase", "REST APIs", "SQLite"],
    features: ["Cross-platform compatibility", "Native performance", "Offline functionality", "Push notifications", "Biometric authentication"],
    benefits: ["Faster time to market", "Single codebase for iOS & Android", "Cost-effective development", "Consistent user experience"]
  },
  {
    id: "backend",
    icon: Server,
    title: "backendDev",
    description: "backendDevDesc",
    gradient: "from-purple-500 to-purple-600",
    technologies: ["Go", "PostgreSQL", "Redis", "Docker", "Kubernetes"],
    features: ["Microservices architecture", "API development", "Database optimization", "Caching strategies", "Security implementation"],
    benefits: ["High performance", "Scalable infrastructure", "Robust security", "Easy maintenance"]
  },
  {
    id: "web",
    icon: Globe,
    title: "webDev",
    description: "webDevDesc",
    gradient: "from-cyan-500 to-cyan-600",
    technologies: ["React", "TypeScript", "Tailwind CSS", "Next.js", "Vite"],
    features: ["Responsive design", "SEO optimization", "Performance optimization", "Progressive Web Apps", "Modern frameworks"],
    benefits: ["Better user engagement", "Improved search rankings", "Fast loading times", "Mobile-first approach"]
  },
  {
    id: "ai",
    icon: Brain,
    title: "aiSolutions",
    description: "aiSolutionsDesc",
    gradient: "from-emerald-500 to-emerald-600",
    badge: "comingSoon",
    technologies: ["Python", "TensorFlow", "PyTorch", "OpenAI APIs", "Machine Learning"],
    features: ["Natural language processing", "Computer vision", "Predictive analytics", "Automation workflows", "Custom AI models"],
    benefits: ["Process automation", "Data-driven insights", "Enhanced user experience", "Competitive advantage"]
  },
  {
    id: "design",
    icon: Palette,
    title: "designBranding",
    description: "designBrandingDesc",
    gradient: "from-orange-500 to-orange-600",
    technologies: ["Adobe Creative Suite", "Figma", "Sketch", "After Effects", "Blender"],
    features: ["Brand identity design", "Logo creation", "Motion graphics", "UI/UX design", "Marketing materials"],
    benefits: ["Strong brand presence", "Professional appearance", "Better user engagement", "Memorable visual identity"]
  },
  {
    id: "consulting",
    icon: Handshake,
    title: "consulting",
    description: "consultingDesc",
    gradient: "from-pink-500 to-pink-600",
    technologies: ["Strategic Planning", "Technology Assessment", "Digital Transformation", "Project Management"],
    features: ["Technology roadmap", "Digital strategy", "Process optimization", "Team training", "Best practices"],
    benefits: ["Strategic direction", "Risk mitigation", "Cost optimization", "Improved efficiency"]
  },
];

export default function AllServices() {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  return (
    <div className="min-h-screen" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      <section className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t("servicesTitle")} {t("servicesTitleHighlight") && <span className="gradient-text">{t("servicesTitleHighlight")}</span>}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t("servicesDescription")}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="group glass-effect hover:shadow-2xl transition-all duration-300 hover:scale-105 h-full">
                  <CardContent className={`p-8 ${isRTL ? 'text-right' : ''}`}>
                    <div className={`bg-gradient-to-br ${service.gradient} p-4 rounded-xl w-fit mb-6 group-hover:shadow-lg transition-shadow duration-300 ${isRTL ? 'mr-0 ml-auto' : ''}`}>
                      <service.icon className="text-white h-6 w-6" />
                    </div>

                    <div className={`flex items-center gap-2 mb-4 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                      <h3 className="text-2xl font-bold">{t(service.title)}</h3>
                      {service.badge && (
                        <Badge className="bg-gradient-to-r from-primary to-purple-500 text-white text-xs">
                          {t(service.badge)}
                        </Badge>
                      )}
                    </div>

                    <p className="text-muted-foreground mb-6">
                      {t(service.description)}
                    </p>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="ghost"
                          className={`text-primary hover:text-primary/80 font-semibold p-0 h-auto ${isRTL ? 'flex-row-reverse' : ''}`}
                        >
                          {t("learnMore")}
                          <ArrowRight className={`h-4 w-4 ${isRTL ? 'rotate-180 mr-2' : 'ml-2'}`} />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" dir={isRTL ? 'rtl' : 'ltr'}>
                        <DialogHeader>
                          <DialogTitle className={`text-2xl font-bold flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div className={`bg-gradient-to-br ${service.gradient} p-3 rounded-lg`}>
                              <service.icon className="text-white h-6 w-6" />
                            </div>
                            {t(service.title)}
                          </DialogTitle>
                        </DialogHeader>

                        <div className="space-y-6">
                          <p className={`text-muted-foreground text-lg ${isRTL ? 'text-right' : ''}`}>
                            {t(service.description)}
                          </p>

                          <div className="grid md:grid-cols-3 gap-6">
                            <div className={isRTL ? 'text-right' : ''}>
                              <h4 className="font-semibold text-lg mb-3">{t("technologies")}</h4>
                              <div className={`space-y-2 ${isRTL ? 'flex flex-wrap justify-end gap-2' : ''}`}>
                                {service.technologies.map((tech, idx) => (
                                  <Badge key={idx} variant="secondary" className={isRTL ? 'ml-2 mb-2' : 'mr-2 mb-2'}>
                                    {tech}
                                  </Badge>
                                ))}
                              </div>
                            </div>

                            <div className={isRTL ? 'text-right' : ''}>
                              <h4 className="font-semibold text-lg mb-3">{t("features")}</h4>
                              <ul className="space-y-2">
                                {service.features.map((feature, idx) => (
                                  <li key={idx} className={`flex items-start gap-2 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className={isRTL ? 'text-right' : ''}>
                              <h4 className="font-semibold text-lg mb-3">{t("benefits")}</h4>
                              <ul className="space-y-2">
                                {service.benefits.map((benefit, idx) => (
                                  <li key={idx} className={`flex items-start gap-2 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                    {benefit}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          <div className={`pt-4 ${isRTL ? 'text-right' : ''}`}>
                            <Button size="lg" className="w-full md:w-auto">
                              {t("requestQuote")}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}